import { useMemo } from 'react';
import { Duration } from 'luxon';
import { useIntl, MessageDescriptor } from 'react-intl';
import { DURATION_HH_MM_SS } from 'utils/date-time';
export const getObject = (
  duration: number
): { hours: number; minutes: number; seconds: number } => {
  const hours =
    duration < 0 ? Math.ceil(duration / 3600) : Math.floor(duration / 3600);
  const minutes =
    duration < 0
      ? hours < 0
        ? Math.abs(Math.ceil(duration / 60) - hours * 60)
        : Math.ceil(duration / 60) - hours * 60
      : Math.floor(duration / 60) - hours * 60;
  const seconds = duration % 60;
  return { hours, minutes, seconds };
};

export const getRoundedDuration = (duration: number): number => {
  const { seconds } = getObject(duration);

  return seconds > 30
    ? Duration.fromObject({ seconds: duration }).plus({ seconds: 60 }).seconds
    : seconds < -30
    ? Duration.fromObject({ seconds: duration }).minus({ seconds: 60 }).seconds
    : duration;
};

export type UseFormattedDurationType = {
  longDurationFormat?: boolean;
  duration: number;
  needsLeadingZeroes?: boolean;
  needsMask?: boolean;
};

type getFormattedDurationProps = UseFormattedDurationType & {
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, any>
  ) => string;
};

export const getFormattedDuration = ({
  formatMessage,
  duration,
  needsLeadingZeroes,
  needsMask,
  longDurationFormat
}: getFormattedDurationProps): string => {
  const { hours, minutes } = getObject(getRoundedDuration(duration));

  const formattedDurationWithoutMask = needsLeadingZeroes
    ? duration < 0
      ? `${hours < -9 ? hours : `-0${Math.abs(hours)}`}:${
          minutes > 9 || minutes < -9
            ? Math.abs(minutes)
            : `0${Math.abs(minutes)}`
        }`
      : `${hours > 9 ? hours : `0${hours}`}:${
          minutes > 9 ? minutes : `0${minutes}`
        }`
    : duration < 0
    ? `${hours === 0 ? '-0' : hours}:${Math.abs(minutes)}`
    : `${hours}:${minutes}`;

  const formattedDurationWithMask =
    hours > 0 || hours <= -1 ? `${hours}h ${minutes}m` : `${minutes}m`;

  const shortFormattedDuration = needsMask
    ? formattedDurationWithMask
    : formattedDurationWithoutMask;

  const longDuration =
    duration < 0
      ? `-${Duration.fromObject({ seconds: Math.abs(duration) || 0 }).toFormat(
          DURATION_HH_MM_SS
        )}`
      : Duration.fromObject({ seconds: duration || 0 }).toFormat(
          DURATION_HH_MM_SS
        );

  const longFormattedDuration = needsMask
    ? formatMessage(
        { id: 'note.durationWithHours' },
        {
          duration: longDuration
        }
      )
    : longDuration;

  return longDurationFormat ? longFormattedDuration : shortFormattedDuration;
};

export const useFormattedDuration = (
  props: UseFormattedDurationType
): string => {
  const { formatMessage } = useIntl();
  return useMemo(() => {
    return getFormattedDuration({ formatMessage, ...props });
  }, [formatMessage, props]);
};
